import React, { useCallback } from "react";
import { axios_tenant } from "../../../../axios";
import { BRAND_COLORS } from "../constants/taskStatuses";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Checkbox, Typography, Box, Link, TableSortLabel, IconButton, CircularProgress, Tooltip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OAPagination from "./OAPagination";
import { TASK_TABLE_COLUMNS, PACKAGE_STATUS_COLOR_MAP } from "../constants/taskStatuses";
import { formatOADateTime, formatDateDDMMMYYYY } from "../utils/dateUtils";
import { formatActivityLabel } from "../utils/activityLabels";

/**
 * Shared paginated task table.
 * Supports selection (via onSelect), custom action column, and click-through navigation.
 * Sorting is server-side: the parent controls sortBy/sortOrder and handles API calls.
 *
 * @param {{
 *   tasks: Array, totalRecords: number, page: number, rowsPerPage: number,
 *   onPageChange: Function, onRowsPerPageChange: Function,
 *   onTaskClick: Function, selectable?: boolean, selectedIds?: Array,
 *   onSelect?: Function, columns?: Array,
 *   sortBy?: string, sortOrder?: string, onSortChange?: Function
 * }} props
 */
const TaskTable = ({
    tasks = [],
    totalRecords = 0,
    page = 0,
    rowsPerPage = 10,
    onPageChange,
    onRowsPerPageChange,
    onTaskClick,
    selectable = false,
    selectedIds = [],
    onSelect,
    loading = false,
    columns = TASK_TABLE_COLUMNS,
    enableLink = true,
    // Optional predicate to visually highlight a row (e.g. Rush tasks — COL-135)
    highlightRow,
    // Server-side sorting props (optional — falls back to client-side if not provided)
    sortBy,
    sortOrder,
    onSortChange
}) => {
    const isServerSorted = Boolean(onSortChange);
    // Client-side sorting state (used only when server-side sorting is not provided)
    const [localOrder, setLocalOrder] = React.useState('asc');
    const [localOrderBy, setLocalOrderBy] = React.useState('taskId');
    const [downloadingTaskId, setDownloadingTaskId] = React.useState(null);

    // Resolve active sort values — prefer server-side props, fall back to local state
    const activeOrderBy = isServerSorted ? sortBy : localOrderBy;
    const activeOrder = isServerSorted ? sortOrder : localOrder;

    const handleRequestSort = (property) => {
        if (isServerSorted) {
            onSortChange(property);
        } else {
            const isAsc = localOrderBy === property && localOrder === 'asc';
            setLocalOrder(isAsc ? 'desc' : 'asc');
            setLocalOrderBy(property);
        }
    };

    const descendingComparator = (a, b, orderBy) => {
        if (b[orderBy] < a[orderBy]) return -1;
        if (b[orderBy] > a[orderBy]) return 1;
        return 0;
    };

    const getComparator = (order, orderBy) => {
        return order === 'desc'
            ? (a, b) => descendingComparator(a, b, orderBy)
            : (a, b) => -descendingComparator(a, b, orderBy);
    };

    // When server-side sorting is active, tasks are already sorted — skip client sort
    const sortedTasks = React.useMemo(() => {
        if (isServerSorted) return tasks;
        return [...tasks].sort(getComparator(localOrder, localOrderBy));
    }, [tasks, localOrder, localOrderBy, isServerSorted]);
    const handleSelectAll = (event) => {
        if (!onSelect) return;
        if (event.target.checked) {
            onSelect(tasks.map((t) => t.taskId));
        } else {
            onSelect([]);
        }
    };

    const handleSelectOne = (taskId) => {
        if (!onSelect) return;
        const idx = selectedIds.indexOf(taskId);
        if (idx === -1) {
            onSelect([...selectedIds, taskId]);
        } else {
            onSelect(selectedIds.filter((id) => id !== taskId));
        }
    };

    const renderCell = (task, column) => {
        // Custom column renderer takes priority (used by PGS action buttons)
        if (column.renderCell) {
            return column.renderCell(task);
        }
        const value = task[column.id];
        switch (column.id) {
            case "applicationNo":
                if (!enableLink) {
                    return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{value}</Typography>;
                }
                return (
                    <Link
                        component="button"
                        onClick={(e) => { e.stopPropagation(); onTaskClick?.(task.taskId); }}
                        underline="hover"
                        sx={{ fontSize: "14px", fontWeight: 500, color: "#1976D2" }}
                        data-testid={`task-link-${task.taskId}`}
                    >
                        {value}
                    </Link>
                );
            case "taskStatus":
                return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{value}</Typography>;
            case "activityName":
                return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{formatActivityLabel(value)}</Typography>;
            case "packageStatus":
                if (!value) return "—";
                const pkgColor = PACKAGE_STATUS_COLOR_MAP[value] || { text: "#334155" };
                return (
                    <Typography sx={{ color: pkgColor.text, fontSize: "14px", fontWeight: 500 }}>
                        {value}
                    </Typography>
                );
            case "package":
                const hasPackages = task.taskStatus === "Completed" && task.packages && task.packages.length > 0;
                const isDownloading = downloadingTaskId === task.taskId;
                if (hasPackages) {
                    const handleDownload = async (e) => {
                        e.stopPropagation();
                        if (isDownloading) return;
                        try {
                            setDownloadingTaskId(task.taskId);
                            const sortedPkgs = [...(task.packages || [])].sort((a, b) => b.version - a.version);
                            const latestPkg = sortedPkgs[0];
                            const downloadFileName = latestPkg?.fileName || `${task.taskId}_package.zip`;

                            const res = await axios_tenant.get(
                                `/collaboration/workflow/tasks/${task.taskId}/package/latest`,
                                { responseType: "blob" }
                            );
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = downloadFileName;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                        } catch (err) {
                            console.error("Download failed:", err);
                        } finally {
                            setDownloadingTaskId(null);
                        }
                    };
                    return (
                        <Tooltip title="Download Package">
                            <IconButton
                                onClick={handleDownload}
                                disabled={isDownloading}
                                size="small"
                                sx={{ color: BRAND_COLORS.PRIMARY }}
                                data-testid={`task-download-${task.taskId}`}
                            >
                                {isDownloading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    <DownloadIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    );
                }
                return <Typography sx={{ fontSize: "14px", color: "#94A3B8" }}>—</Typography>;

            case "requestedDate":
            case "oaMailDate":
                return (
                    <Typography sx={{ fontSize: "14px", color: "#334155" }}>
                        {formatDateDDMMMYYYY(value)}
                    </Typography>
                );
            case "deadlineDate": {
                // COL-138/135: bold red once the deadline is reached ("Due Today")
                // or exceeded ("Overdue"). Completed tasks are never flagged.
                if (!value) return <Typography sx={{ fontSize: "14px", color: "#94A3B8" }}>—</Typography>;
                const today = new Date(); today.setHours(0, 0, 0, 0);
                const dl = new Date(`${String(value).slice(0, 10)}T00:00:00`);
                const valid = !isNaN(dl.getTime());
                const flagged = valid && dl <= today && task.taskStatus !== "Completed";
                const flagLabel = flagged ? (dl < today ? "Overdue" : "Due Today") : null;
                return (
                    <Typography sx={{ fontSize: "14px", fontWeight: flagged ? 700 : 400, color: flagged ? "#DC2626" : "#334155" }}>
                        {formatDateDDMMMYYYY(value)}
                        {flagLabel && <span style={{ display: "block", fontSize: "11px", fontWeight: 600 }}>{flagLabel}</span>}
                    </Typography>
                );
            }
            case "createdAt":
                if (!value) return "—";
                return (
                    <Typography sx={{ fontSize: "14px", color: "#334155" }}>
                        {formatOADateTime(value)}
                    </Typography>
                );
            case "processorName":
                if (value === "Processing Skipped") {
                    return <Typography sx={{ fontSize: "14px", color: "#6366F1", fontStyle: "italic", fontWeight: 500 }}>{value}</Typography>;
                }
                return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{value || "—"}</Typography>;
            default:
                return <Typography sx={{ fontSize: "14px", color: "#334155" }}>{value || "—"}</Typography>;
        }
    };

    // COL-133: size the table to its columns so they don't get cramped as more
    // columns/rows are shown — the container scrolls horizontally when needed.
    const tableMinWidth = (selectable ? 48 : 0) + columns.reduce((sum, c) => sum + (c.minWidth || 120), 0);

    // flex "0 1 auto": the table hugs its content so a short result set doesn't
    // leave dead space inside the border, but still shrinks (and scrolls) when
    // the rows exceed the available height.
    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", minHeight: 0, flex: "0 1 auto", maxHeight: "100%" }} data-testid="oa-task-table">
            <TableContainer sx={{ border: "1px solid #E2E8F0", borderRadius: 2, backgroundColor: "#fff", flex: "0 1 auto", maxHeight: "100%", overflow: "auto" }}>
                <Table stickyHeader size="small" sx={{ minWidth: tableMinWidth }}>
                    <TableHead>
                        <TableRow>
                            {selectable && (
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < tasks.length}
                                        checked={tasks.length > 0 && selectedIds.length === tasks.length}
                                        onChange={handleSelectAll}
                                        data-testid="oa-task-table-select-all"
                                        sx={{
                                            color: BRAND_COLORS.BORDER_LIGHT,
                                            '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` },
                                            '&.MuiCheckbox-indeterminate': { color: `${BRAND_COLORS.PRIMARY} !important` }
                                        }}
                                    />
                                </TableCell>
                            )}
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    sx={{
                                        minWidth: col.minWidth,
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        color: "#64748B",
                                        backgroundColor: "#F8FAFC",
                                        borderBottom: '1px solid #E2E8F0',
                                        whiteSpace: 'nowrap',
                                        ...(col.id === 'actions' ? {
                                            position: 'sticky',
                                            top: 0,
                                            right: 0,
                                            zIndex: 4,
                                            backgroundColor: '#F8FAFC',
                                            boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.1)'
                                        } : {})
                                    }}
                                    sortDirection={activeOrderBy === col.id ? activeOrder : false}
                                >
                                    <TableSortLabel
                                        active={activeOrderBy === col.id}
                                        direction={activeOrderBy === col.id ? activeOrder : 'asc'}
                                        onClick={() => handleRequestSort(col.id)}
                                        IconComponent={() => (
                                            <span className="sort-arrow-icon" style={{ fontSize: '14px', marginLeft: '4px' }}>
                                                {activeOrderBy === col.id ? (activeOrder === 'desc' ? '↓' : '↑') : '↓'}
                                            </span>
                                        )}
                                        sx={{
                                            '&:hover': { color: 'inherit' },
                                            '&.Mui-active': { color: 'inherit' },
                                            '& .sort-arrow-icon': {
                                                color: '#666',
                                                opacity: activeOrderBy === col.id ? 1 : 0,
                                                transition: 'opacity 0.15s'
                                            },
                                            '&:hover .sort-arrow-icon': {
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tasks.length === 0 ? (
                            !loading && (
                                <TableRow>
                                    <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center">
                                        <Typography color="text.secondary" sx={{ py: 4 }}>No tasks found</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        ) : (
                            tasks.map((task) => {
                                const isHighlighted = highlightRow ? highlightRow(task) : false;
                                return (
                                <TableRow
                                    key={task.taskId}
                                    hover
                                    sx={{
                                        cursor: enableLink ? "pointer" : "default",
                                        ...(isHighlighted && { backgroundColor: "#FEF2F2", borderLeft: "3px solid #DC2626" }),
                                        "&:hover": { backgroundColor: isHighlighted ? "#FEE2E2" : "#F8FAFC" }
                                    }}
                                    data-testid={`oa-task-row-${task.taskId}`}
                                >
                                    {selectable && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedIds.includes(task.taskId)}
                                                onChange={() => handleSelectOne(task.taskId)}
                                                data-testid={`oa-task-checkbox-${task.taskId}`}
                                                sx={{
                                                    color: BRAND_COLORS.BORDER_LIGHT,
                                                    '&.Mui-checked': { color: `${BRAND_COLORS.PRIMARY} !important` }
                                                }}
                                            />
                                        </TableCell>
                                    )}
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.id}
                                            sx={{
                                                ...(col.id === 'actions' ? {
                                                    position: 'sticky',
                                                    right: 0,
                                                    zIndex: 2,
                                                    backgroundColor: '#fff',
                                                    boxShadow: '-2px 0 4px -2px rgba(0,0,0,0.1)',
                                                    'tr:hover &': {
                                                        backgroundColor: '#F8FAFC'
                                                    }
                                                } : {})
                                            }}
                                        >
                                            {renderCell(task, col)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalRecords > 10 && (
                <Box sx={{ flexShrink: 0 }}>
                    <OAPagination
                        count={totalRecords}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={onPageChange}
                        onRowsPerPageChange={onRowsPerPageChange}
                    />
                </Box>
            )}
        </Box>
    );
};

export default TaskTable;
